import { RestApiClient, MemoryTokenManager } from '@docyrus/api-client';

import { DEFAULT_API_URL, DEFAULT_SSR_COOKIE_KEY } from '../constants';
import { type ReadonlyCookieStore, type ServerClientConfig } from './types';

/**
 * Create an authenticated `RestApiClient` for use in Next.js
 * Server Components, Server Actions, and Route Handlers.
 *
 * Reads the access token from cookies. The client cannot refresh tokens —
 * that is handled client-side by `DocyrusAuthProvider`.
 *
 * @example Server Component
 * ```ts
 * import { cookies } from 'next/headers';
 * import { createServerClient } from '@docyrus/signin/nextjs';
 *
 * export default async function Page() {
 *   const client = createServerClient(await cookies());
 *   const { data: user } = await client.get('/v1/users/me');
 *   return <div>{user.name}</div>;
 * }
 * ```
 *
 * @example Server Action
 * ```ts
 * 'use server';
 * import { cookies } from 'next/headers';
 * import { createServerClient } from '@docyrus/signin/nextjs';
 *
 * export async function getUser() {
 *   const client = createServerClient(await cookies());
 *   return client.get('/v1/users/me');
 * }
 * ```
 */
export function createServerClient(
  cookieStore: ReadonlyCookieStore,
  config: ServerClientConfig = {}
): RestApiClient {
  const apiUrl = config.apiUrl ?? DEFAULT_API_URL;
  const cookieKey = config.cookieKey ?? DEFAULT_SSR_COOKIE_KEY;

  const token = cookieStore.get(cookieKey)?.value ?? null;
  const decodedToken = token ? decodeURIComponent(token) : null;

  const tokenManager = new MemoryTokenManager();

  if (decodedToken) {
    tokenManager.setToken(decodedToken);
  }

  return new RestApiClient({
    baseURL: apiUrl,
    tokenManager
  });
}
